import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import '../styles/v2.css';

/* ── Password strength ── */
function getStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '#e2e8f0' };
  let score = 0;
  if (pw.length >= 8)  score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Weak',   color: '#ef4444', pct: 25 };
  if (score <= 2) return { score, label: 'Fair',   color: '#f59e0b', pct: 50 };
  if (score <= 3) return { score, label: 'Good',   color: '#10b981', pct: 75 };
  return              { score, label: 'Strong', color: '#059669', pct: 100 };
}

/* ── Debounce hook ── */
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/* ── Field status icon ── */
function FieldStatus({ status }) {
  if (status === 'ok') return (
    <div style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', width:18, height:18, borderRadius:'50%', background:'#10b981', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg style={{ width:11, height:11, color:'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
    </div>
  );
  if (status === 'error') return (
    <div style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', width:18, height:18, borderRadius:'50%', background:'#ef4444', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg style={{ width:11, height:11, color:'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
    </div>
  );
  return null;
}

export default function RegisterV2() {
  const navigate = useNavigate();

  const [shopName, setShopName]     = useState('');
  const [username, setUsername]     = useState('');
  const [password, setPassword]     = useState('');
  const [email, setEmail]           = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [error, setError]           = useState('');
  const [fieldErrors, setFieldErrors] = useState({ username: '', email: '' });
  const [loading, setLoading]       = useState(false);
  const [success, setSuccess]       = useState(false);

  const debouncedUsername = useDebounce(username, 500);
  const debouncedEmail    = useDebounce(email, 500);

  /* real-time duplicate checks */
  useEffect(() => {
    if (debouncedUsername.length > 2) {
      axiosInstance.get(`/auth/check-duplicates?username=${encodeURIComponent(debouncedUsername)}`)
        .then(r => setFieldErrors(p => ({ ...p, username: r.data.username ? 'Username already taken' : '' })))
        .catch(() => {});
    } else {
      setFieldErrors(p => ({ ...p, username: '' }));
    }
  }, [debouncedUsername]);

  useEffect(() => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (debouncedEmail && re.test(debouncedEmail)) {
      axiosInstance.get(`/auth/check-duplicates?email=${encodeURIComponent(debouncedEmail)}`)
        .then(r => setFieldErrors(p => ({ ...p, email: r.data.email ? 'Email already registered' : '' })))
        .catch(() => {});
    } else {
      setFieldErrors(p => ({ ...p, email: '' }));
    }
  }, [debouncedEmail]);

  const strength = getStrength(password);
  const emailRe  = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const hasFieldErr = !!fieldErrors.username || !!fieldErrors.email;

  const usernameStatus = fieldErrors.username ? 'error' : (username.length > 2 && !fieldErrors.username ? 'ok' : null);
  const emailStatus    = fieldErrors.email ? 'error' : (email && emailRe.test(email) && !fieldErrors.email ? 'ok' : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (hasFieldErr) { setError('Please fix the errors below before continuing.'); return; }
    if (!shopName.trim())   { setError('Shop name is required.'); return; }
    if (!username.trim())   { setError('Username is required.'); return; }
    if (!password.trim())   { setError('Password is required.'); return; }
    if (email.trim() && !emailRe.test(email)) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    try {
      await axiosInstance.post('/auth/register', { shop_name: shopName, username, password, email });
      setSuccess(true);
      setTimeout(() => navigate('/new/login'), 3000);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.message || d?.error || d?.detail || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  /* ── Feature bullets for left panel ── */
  const features = [
    { icon: '📦', text: 'Track inventory across all your products in real time' },
    { icon: '📊', text: 'See daily sales, profits, and business insights' },
    { icon: '🤖', text: 'AI-powered product recognition from photos' },
    { icon: '📱', text: 'Works beautifully on your phone, anywhere' },
    { icon: '🇿🇲', text: 'Built specifically for Zambian shop owners' },
  ];

  return (
    <div className="v2-login-root">

      {/* ── Left brand panel ── */}
      <div className="v2-login-left v2-mesh-bg" style={{ width:'52%', display:'flex', flexDirection:'column', justifyContent:'space-between', padding:56, overflow:'hidden', position:'relative' }}>
        {[640,460,280].map((s,i)=>(
          <div key={i} style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:s, height:s, borderRadius:'50%', border:`1px solid rgba(16,185,129,${0.1-i*0.02})`, pointerEvents:'none' }} />
        ))}
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-55%)', fontSize:300, fontWeight:900, color:'rgba(255,255,255,0.018)', lineHeight:1, pointerEvents:'none', userSelect:'none' }}>ZS</div>

        {/* Logo + headline */}
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:44 }}>
            <div style={{ width:44, height:44, borderRadius:16, background:'linear-gradient(135deg,#34d399,#059669)', boxShadow:'0 8px 24px rgba(16,185,129,0.4)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'white', fontWeight:900, fontSize:15 }}>ZS</span>
            </div>
            <div>
              <p style={{ color:'white', fontWeight:700, fontSize:17, lineHeight:1, letterSpacing:'-0.02em' }}>ZambiaShoppe</p>
              <p style={{ color:'rgba(16,185,129,0.6)', fontSize:11, marginTop:3 }}>Inventory Management Platform</p>
            </div>
          </div>
          <h1 style={{ fontSize:42, fontWeight:900, lineHeight:1.1, letterSpacing:'-0.03em', marginBottom:16 }}>
            <span className="v2-gradient-headline">Start managing<br/>your shop today.</span>
          </h1>
          <p style={{ color:'rgba(209,250,229,0.5)', fontSize:15, lineHeight:1.65, maxWidth:300, marginBottom:36 }}>
            Join shop owners across Zambia using ZambiaShoppe to grow their business.
          </p>

          {/* Feature list */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {features.map((f,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:36, height:36, borderRadius:10, background:'rgba(16,185,129,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>{f.icon}</div>
                <span style={{ color:'rgba(255,255,255,0.65)', fontSize:13.5, lineHeight:1.4 }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Trust bar */}
        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ height:1, background:'linear-gradient(90deg,transparent,rgba(16,185,129,0.25),transparent)', marginBottom:20 }} />
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div className="v2-pulse" style={{ width:6, height:6, borderRadius:'50%', background:'#10b981' }} />
            <span style={{ color:'rgba(16,185,129,0.55)', fontSize:11 }}>Trusted by shop owners across Zambia 🇿🇲</span>
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'#f7faf7', overflowY:'auto', padding:'40px 32px', position:'relative' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(circle,rgba(0,0,0,0.04) 1px,transparent 1px)', backgroundSize:'22px 22px', opacity:0.8, pointerEvents:'none' }} />

        <div style={{ width:'100%', maxWidth:400, position:'relative' }}>

          {/* Mobile logo */}
          <div className="v2-mobile-logo" style={{ display:'flex', alignItems:'center', gap:12, marginBottom:32 }}>
            <div style={{ width:40, height:40, borderRadius:16, background:'linear-gradient(135deg,#34d399,#059669)', boxShadow:'0 6px 20px rgba(16,185,129,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'white', fontWeight:900, fontSize:14 }}>ZS</span>
            </div>
            <span style={{ color:'#0f172a', fontWeight:700, fontSize:18 }}>ZambiaShoppe</span>
          </div>

          {/* Success state */}
          {success ? (
            <div style={{ textAlign:'center', padding:'32px 0' }}>
              <div style={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#34d399,#059669)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                <svg style={{ width:36, height:36, color:'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
              </div>
              <h2 style={{ color:'#0f172a', fontSize:22, fontWeight:900, letterSpacing:'-0.02em', margin:'0 0 10px' }}>Account created!</h2>
              <p style={{ color:'#64748b', fontSize:14, lineHeight:1.6, margin:'0 0 24px' }}>
                Welcome to ZambiaShoppe. Taking you to sign in…
              </p>
              <div style={{ height:4, background:'#e2e8f0', borderRadius:99, overflow:'hidden' }}>
                <div style={{ height:'100%', background:'linear-gradient(90deg,#10b981,#059669)', borderRadius:99, animation:'progress 3s linear forwards' }} />
              </div>
              <style>{`@keyframes progress { from { width:0 } to { width:100% } }`}</style>
            </div>
          ) : (
            <>
              {/* Heading */}
              <div style={{ marginBottom:28 }}>
                <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 12px', borderRadius:99, background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.18)', marginBottom:16 }}>
                  <div className="v2-pulse" style={{ width:6, height:6, borderRadius:'50%', background:'#10b981' }} />
                  <span style={{ color:'#065f46', fontSize:11, fontWeight:600, letterSpacing:'0.04em' }}>Free account — no credit card needed</span>
                </div>
                <h2 style={{ color:'#0f172a', fontSize:26, fontWeight:900, letterSpacing:'-0.03em', margin:'0 0 8px' }}>Create your account</h2>
                <p style={{ color:'#94a3b8', fontSize:14, lineHeight:1.5, margin:0 }}>Set up your shop in under a minute.</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>

                {/* Shop Name */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:13, fontWeight:700, color:'#475569' }}>Shop Name *</label>
                  <div style={{ position:'relative' }}>
                    <svg style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:'#94a3b8', pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" /></svg>
                    <input className="v2-input" type="text" placeholder="e.g. Chanda's General Store" value={shopName} onChange={e=>{setShopName(e.target.value);setError('');}} autoFocus required />
                  </div>
                </div>

                {/* Username */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:13, fontWeight:700, color:'#475569' }}>Username *</label>
                  <div style={{ position:'relative' }}>
                    <svg style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:'#94a3b8', pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                    <input className="v2-input" type="text" placeholder="Choose a username" value={username}
                      onChange={e=>{setUsername(e.target.value);setError('');}}
                      style={{ paddingRight:40, borderColor: fieldErrors.username ? '#ef4444' : undefined }} autoComplete="username" required />
                    <FieldStatus status={usernameStatus} />
                  </div>
                  {fieldErrors.username && <p style={{ fontSize:11, color:'#ef4444', margin:0, fontWeight:500 }}>{fieldErrors.username}</p>}
                </div>

                {/* Password */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:13, fontWeight:700, color:'#475569' }}>Password *</label>
                  <div style={{ position:'relative' }}>
                    <svg style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:'#94a3b8', pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                    <input className="v2-input v2-input-pr" type={showPw?'text':'password'} placeholder="Create a strong password" value={password} onChange={e=>{setPassword(e.target.value);setError('');}} autoComplete="new-password" required />
                    <button type="button" onClick={()=>setShowPw(s=>!s)} style={{ position:'absolute', right:13, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', display:'flex', padding:0 }}>
                      {showPw
                        ? <svg style={{ width:17, height:17 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>
                        : <svg style={{ width:17, height:17 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      }
                    </button>
                  </div>
                  {/* Strength bar */}
                  {password && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:2 }}>
                      <div style={{ flex:1, height:4, background:'#e2e8f0', borderRadius:99, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${strength.pct}%`, background:strength.color, borderRadius:99, transition:'width 0.3s ease, background 0.3s ease' }} />
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:strength.color, width:42, textAlign:'right', flexShrink:0 }}>{strength.label}</span>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:13, fontWeight:700, color:'#475569' }}>
                    Email <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional — used for password recovery)</span>
                  </label>
                  <div style={{ position:'relative' }}>
                    <svg style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:16, height:16, color:'#94a3b8', pointerEvents:'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                    <input className="v2-input" type="email" placeholder="you@example.com" value={email}
                      onChange={e=>{setEmail(e.target.value);setError('');}}
                      style={{ paddingRight:40, borderColor: fieldErrors.email ? '#ef4444' : undefined }} autoComplete="email" />
                    <FieldStatus status={emailStatus} />
                  </div>
                  {fieldErrors.email && <p style={{ fontSize:11, color:'#ef4444', margin:0, fontWeight:500 }}>{fieldErrors.email}</p>}
                </div>

                {/* General error */}
                {error && (
                  <div style={{ padding:'12px 14px', borderRadius:10, background:'#fef2f2', border:'1px solid #fecaca' }}>
                    <p style={{ margin:0, fontSize:13, color:'#b91c1c', fontWeight:500 }}>{error}</p>
                  </div>
                )}

                <button type="submit" className="v2-btn-submit" disabled={loading || hasFieldErr} style={{ marginTop:4, fontSize:15 }}>
                  {loading ? 'Creating account…' : 'Create Account'}
                </button>
              </form>

              {/* Divider */}
              <div style={{ position:'relative', margin:'24px 0' }}>
                <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center' }}>
                  <div style={{ width:'100%', borderTop:'1px solid #e2e8f0' }} />
                </div>
                <div style={{ position:'relative', display:'flex', justifyContent:'center' }}>
                  <span style={{ background:'#f7faf7', padding:'0 12px', color:'#94a3b8', fontSize:12 }}>Already have an account?</span>
                </div>
              </div>

              <a href="/new/login"
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px 0', borderRadius:12, border:'1.5px solid #d1d9d1', background:'white', color:'#475569', fontSize:14, fontWeight:600, textDecoration:'none', transition:'all 0.15s' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='#10b981';e.currentTarget.style.color='#065f46';e.currentTarget.style.background='#f0fdf8';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#d1d9d1';e.currentTarget.style.color='#475569';e.currentTarget.style.background='white';}}>
                Sign in to your account →
              </a>

              <p style={{ textAlign:'center', color:'#cbd5e1', fontSize:11, marginTop:48, letterSpacing:'0.04em' }}>
                ZambiaShoppe · Secure &amp; encrypted
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
